import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import shopApi from '../services/shopApi';
import './BrandsPage.css';

const BRAND_ICONS = {
  Clothing:       '👕',
  'Luxury Bags':  '👜',
  Vitamins:       '🫐',
  Beverages:      '🥤',
  Snacks:         '🍜',
  Medicine:       '💊',
  Beauty:         '💄',
  Electronics:    '🔌',
  Footwear:       '👟',
  default:        '🏷️',
};

export default function BrandsPage() {
  const [tab, setTab]               = useState('brands');   // 'brands' | 'sellers'

  // Product brands tab state
  const [productBrands, setProductBrands] = useState([]);
  const [pbLoading, setPbLoading]   = useState(true);
  const [pbSearch, setPbSearch]     = useState('');
  const [pbCategory, setPbCategory] = useState('');
  const [categories, setCategories] = useState([]);

  // Sellers tab state
  const [sellers, setSellers]       = useState([]);
  const [industries, setIndustries] = useState([]);
  const [selLoading, setSelLoading] = useState(true);
  const [selSearch, setSelSearch]   = useState('');
  const [selIndustry, setSelIndustry] = useState('');

  // Load product brands + categories
  useEffect(() => {
    Promise.all([
      shopApi.getProductBrands({ limit: 50 }).catch(() => ({ success: false })),
      shopApi.getCategories().catch(() => ({ success: false })),
    ]).then(([pb, c]) => {
      if (pb.success) setProductBrands(pb.data.brands || []);
      if (c.success)  setCategories(c.data.categories || []);
      setPbLoading(false);
    });
  }, []);

  // Load sellers + industries
  useEffect(() => {
    Promise.all([
      shopApi.getBrands({ limit: 50 }).catch(() => ({ success: false })),
      shopApi.getIndustries().catch(() => ({ success: false })),
    ]).then(([b, ind]) => {
      if (b.success)   setSellers(b.data.brands || []);
      if (ind.success) setIndustries(ind.data.industries || []);
      setSelLoading(false);
    });
  }, []);

  // Filtered product brands
  const filteredBrands = productBrands.filter(b => {
    const matchSearch = !pbSearch || b.brandName?.toLowerCase().includes(pbSearch.toLowerCase());
    const matchCat    = !pbCategory || b.categories?.includes(pbCategory);
    return matchSearch && matchCat;
  });

  // Filtered sellers
  const filteredSellers = sellers.filter(b => {
    const matchSearch = !selSearch || b.companyName?.toLowerCase().includes(selSearch.toLowerCase());
    const matchInd    = !selIndustry || b.industry === selIndustry;
    return matchSearch && matchInd;
  });

  return (
    <div className="brands-page page-wrap">

      {/* HERO */}
      <div className="brands-hero">
        <div className="badge badge-cyan" style={{ marginBottom: 16 }}>Verified Authentic</div>
        <h1>Browse <span className="grad">Brands</span></h1>
        <p>Every brand and seller on JepTags is verified. All products are guaranteed authentic.</p>
      </div>

      {/* TABS */}
      <div className="brands-tabs">
        <button
          className={`brands-tab ${tab === 'brands' ? 'active' : ''}`}
          onClick={() => setTab('brands')}
        >
          🏷️ Product Brands
          <span className="tab-count">{productBrands.length}</span>
        </button>
        <button
          className={`brands-tab ${tab === 'sellers' ? 'active' : ''}`}
          onClick={() => setTab('sellers')}
        >
          🏢 Sellers
          <span className="tab-count">{sellers.length}</span>
        </button>
      </div>

      {/* ── PRODUCT BRANDS TAB ── */}
      {tab === 'brands' && (
        <>
          <div className="brands-filters">
            <div className="brands-search">
              <span>🔍</span>
              <input
                value={pbSearch}
                onChange={e => setPbSearch(e.target.value)}
                placeholder="Search brand names…"
              />
            </div>
            <select
              className="industry-filter"
              value={pbCategory}
              onChange={e => setPbCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c.category} value={c.category}>
                  {c.category} ({c.count})
                </option>
              ))}
            </select>
            {(pbSearch || pbCategory) && (
              <button className="btn-ghost" onClick={() => { setPbSearch(''); setPbCategory(''); }}>✕ Clear</button>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>
              {filteredBrands.length} brands
            </span>
          </div>

          {pbLoading ? <div className="spinner" /> : (
            filteredBrands.length ? (
              <div className="brands-grid">
                {filteredBrands.map(b => (
                  <Link
                    key={b.brandName}
                    to={`/products?brand=${encodeURIComponent(b.brandName)}`}
                    className="brand-card"
                  >
                    <div className="brand-card-head">
                      <div className="brand-card-logo">
                        {b.imageUrl
                          ? <img src={b.imageUrl} alt={b.brandName} />
                          : (BRAND_ICONS[b.categories?.[0]] || BRAND_ICONS.default)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="brand-card-name">{b.brandName}</div>
                        <div className="brand-card-industry">
                          {b.categories?.join(' · ') || 'General'}
                        </div>
                      </div>
                      <div className="brand-card-verified">✓</div>
                    </div>

                    {/* Price range */}
                    {b.priceRange?.min > 0 && (
                      <div className="brand-card-price">
                        {b.priceRange.min === b.priceRange.max
                          ? `From ${b.priceRange.min.toLocaleString()}`
                          : `${b.priceRange.min.toLocaleString()} – ${b.priceRange.max.toLocaleString()}`
                        }
                      </div>
                    )}

                    {/* Sold by */}
                    {b.sellers?.length > 0 && (
                      <div className="brand-card-sellers">
                        Sold by: {b.sellers.map(s => s.name).filter(Boolean).join(', ')}
                      </div>
                    )}

                    <div className="brand-card-footer">
                      <span className="brand-card-count">📦 {b.productCount} {b.productCount === 1 ? 'product' : 'products'}</span>
                      <span className="brand-card-arrow">→</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="icon">🏷️</div>
                <h3>No brands found</h3>
                <p>Try adjusting your search or category filter.</p>
              </div>
            )
          )}
        </>
      )}

      {/* ── SELLERS TAB ── */}
      {tab === 'sellers' && (
        <>
          <div className="brands-filters">
            <div className="brands-search">
              <span>🔍</span>
              <input
                value={selSearch}
                onChange={e => setSelSearch(e.target.value)}
                placeholder="Search sellers…"
              />
            </div>
            <select
              className="industry-filter"
              value={selIndustry}
              onChange={e => setSelIndustry(e.target.value)}
            >
              <option value="">All Industries</option>
              {industries.map(i => (
                <option key={i.industry} value={i.industry}>
                  {i.industry} ({i.brandCount || i.count})
                </option>
              ))}
            </select>
            {(selSearch || selIndustry) && (
              <button className="btn-ghost" onClick={() => { setSelSearch(''); setSelIndustry(''); }}>✕ Clear</button>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>
              {filteredSellers.length} sellers
            </span>
          </div>

          {selLoading ? <div className="spinner" /> : (
            filteredSellers.length ? (
              <div className="brands-grid">
                {filteredSellers.map(b => (
                  <Link key={b._id} to={`/brands/${b.slug || b._id}`} className="brand-card">
                    <div className="brand-card-head">
                      <div className="brand-card-logo">
                        {b.logoUrl ? <img src={b.logoUrl} alt={b.companyName} /> : '🏢'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="brand-card-name">{b.companyName}</div>
                        <div className="brand-card-industry">{b.industry || 'General'}</div>
                      </div>
                      <div className="brand-card-verified">✓</div>
                    </div>
                    {b.description && (
                      <div className="brand-card-desc">{b.description}</div>
                    )}
                    <div className="brand-card-footer">
                      <span className="brand-card-count">📦 {b.productCount || 0} products</span>
                      <span className="brand-card-arrow">→</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="icon">🏢</div>
                <h3>No sellers found</h3>
                <p>Try adjusting your search or industry filter.</p>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}